import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { WithChildrenCommonProps } from '@types';
import { useKeyPress, useToggle, PopoutPlacement } from '@hooks';
import { Input, InputProps } from '@components/form/input';
import { Highlight, defaultHighlightFn } from '@components/highlight';
import { DropdownOptionList, IDropdownOption } from '@components/dropdown-option-list';
import { Button } from '@components/button';
import { Icon, IconNames } from '@components/icon';
import { Option } from '@components/option';
import { getFieldChevronStylesAsClassName } from '../field/field-styles';
import { concatenateAndReplace } from '@utils/test-helpers';
export type ISelectAutocompleteOption = IDropdownOption<unknown>;
export type ISelectAutocomplete = Omit<InputProps, 'name' | 'onChange' | 'value'> & WithChildrenCommonProps<{
  /** Defines the width of the Dropdown element */
  dropdownWidth?: number | string;
  /** Indicates that any value can be used in the input, no need to be one of the options */
  freeInput?: boolean;
  /** Indicates the property to be used as value for the key Autocomplete options */
  /** Warning: If keyId is not defined, index is used and this could cause performance issues */
  keyId?: string;
  /**
   * Indicates if the SelectAutocomplete is opened
   * Note: this prop is used in case you want to open the autocomplete from the application. It is not needed to manage it from the application.
   * It cannot be used to close the autocomplete.
   * */
  isOpen?: boolean;
  /** Indicates the number of items to be displayed in the dropdown when it is opened */
  maxVisibleOptions?: number;
  /** Define the name of the input element */
  name: string;
  /** Defines the label to be displayed when there are no results */
  noResultsLabel: string;
  /** List of options to be displayed in the Dropdown */
  options: ISelectAutocompleteOption[];
  /** Text to display in the header of the option list */
  optionsHeaderText?: string;
  /** Flag to indicate whether the chevron icon is displayed */
  showRightIcon?: boolean;
  /** Indicates the position of the dropdown relative to the Input */
  placement?: PopoutPlacement;
  /** Indicates the value of the Select */
  value?: ISelectAutocompleteOption | null;
  /** Function to be executed when clicking outside the textfield */
  onBlur?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Function to be executed when changing the selected value */
  onChange?: (name: string, value: ISelectAutocompleteOption | null) => void;
  /** Function to be executed when clicking outside the dropdown */
  onClickAway?: (value?: string) => void;
  /** Function to execute on field focus */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** Function to be executed when changing the input */
  onInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Defines if label is left aligned */
  labelLeftAligned?: boolean;
}>;
export const SearchSelectAutocomplete = t0 => {
  const $ = _c(132);
  let dataId;
  let disabled;
  let dropdownWidth;
  let isOpen;
  let keyId;
  let label;
  let leftContent;
  let name;
  let noResultsLabel;
  let onChange;
  let onClear;
  let onClickAway;
  let onFocus;
  let onInputChange;
  let options;
  let optionsHeaderText;
  let placeholder;
  let readOnly;
  let rest;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let value;
  if ($[0] !== t0) {
    ({
      dataId,
      disabled,
      dropdownWidth,
      freeInput: t1,
      leftContent,
      isOpen,
      keyId,
      label,
      maxVisibleOptions: t2,
      name,
      noResultsLabel,
      optionsHeaderText,
      options,
      placeholder,
      placement: t3,
      readOnly,
      showRightIcon: t4,
      value,
      labelLeftAligned: t5,
      onChange,
      onClear,
      onClickAway,
      onFocus,
      onInputChange,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = dataId;
    $[2] = disabled;
    $[3] = dropdownWidth;
    $[4] = isOpen;
    $[5] = keyId;
    $[6] = label;
    $[7] = leftContent;
    $[8] = name;
    $[9] = noResultsLabel;
    $[10] = onChange;
    $[11] = onClear;
    $[12] = onClickAway;
    $[13] = onFocus;
    $[14] = onInputChange;
    $[15] = options;
    $[16] = optionsHeaderText;
    $[17] = placeholder;
    $[18] = readOnly;
    $[19] = rest;
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = value;
  } else {
    dataId = $[1];
    disabled = $[2];
    dropdownWidth = $[3];
    isOpen = $[4];
    keyId = $[5];
    label = $[6];
    leftContent = $[7];
    name = $[8];
    noResultsLabel = $[9];
    onChange = $[10];
    onClear = $[11];
    onClickAway = $[12];
    onFocus = $[13];
    onInputChange = $[14];
    options = $[15];
    optionsHeaderText = $[16];
    placeholder = $[17];
    readOnly = $[18];
    rest = $[19];
    t1 = $[20];
    t2 = $[21];
    t3 = $[22];
    t4 = $[23];
    t5 = $[24];
    value = $[25];
  }
  const freeInput = t1 === undefined ? false : t1;
  const maxVisibleOptions = t2 === undefined ? 5 : t2;
  const placement = t3 === undefined ? "bottom-start" : t3;
  const showRightIcon = t4 === undefined ? true : t4;
  const labelLeftAligned = t5 === undefined ? false : t5;
  const [query, setQuery] = React.useState(undefined);
  const [selectedInputValue, setSelectedInputValue] = React.useState(value?.label || "");
  const [focusedIndex, setFocusedIndex] = React.useState(0);
  const [editionMode, setEditionMode] = React.useState(false);
  const {
    on: isDropdownOpen,
    toggle
  } = useToggle(false);
  const [inputElement, setInputElement] = React.useState(null);
  value?.label;
  const externalValue = value?.label || "";
  let t6;
  if ($[26] !== options || $[27] !== query) {
    t6 = options.filter(option => {
      if (!query) {
        return options;
      }
      return defaultHighlightFn(option.label, query).length > 1;
    });
    $[26] = options;
    $[27] = query;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  const filteredOptions = t6;
  const showDropdown = !readOnly && !disabled && (!freeInput || freeInput && filteredOptions.length > 0) && isDropdownOpen;
  let t7;
  if ($[29] !== disabled || $[30] !== isDropdownOpen || $[31] !== query || $[32] !== readOnly || $[33] !== toggle) {
    t7 = () => {
      if (disabled || readOnly || !query) {
        return;
      }
      !isDropdownOpen && toggle();
    };
    $[29] = disabled;
    $[30] = isDropdownOpen;
    $[31] = query;
    $[32] = readOnly;
    $[33] = toggle;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  const handleOnClick = t7;
  let t8;
  bb0: {
    if (!showRightIcon) {
      t8 = undefined;
      break bb0;
    }
    const iconName = isDropdownOpen ? IconNames.CHEVRON_UP : IconNames.CHEVRON_DOWN;
    let t9;
    if ($[35] !== iconName) {
      t9 = <Icon name={iconName} />;
      $[35] = iconName;
      $[36] = t9;
    } else {
      t9 = $[36];
    }
    let t10;
    if ($[37] !== handleOnClick || $[38] !== t9) {
      t10 = <Button variant="text" onClick={handleOnClick} className={getFieldChevronStylesAsClassName}>{t9}</Button>;
      $[37] = handleOnClick;
      $[38] = t9;
      $[39] = t10;
    } else {
      t10 = $[39];
    }
    t8 = t10;
  }
  const rightIcon = t8;
  let t10;
  let t9;
  if ($[40] !== isOpen || $[41] !== name) {
    t9 = () => {
      if (isOpen) {
        document.getElementById(name)?.focus();
      }
    };
    t10 = [isOpen, name];
    $[40] = isOpen;
    $[41] = name;
    $[42] = t10;
    $[43] = t9;
  } else {
    t10 = $[42];
    t9 = $[43];
  }
  React.useEffect(t9, t10);
  let t11;
  if ($[44] !== handleOnClick || $[45] !== onFocus) {
    t11 = event => {
      handleOnClick();
      onFocus?.(event);
    };
    $[44] = handleOnClick;
    $[45] = onFocus;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  const onFocusInput = t11;
  let t12;
  if ($[47] !== freeInput || $[48] !== isDropdownOpen || $[49] !== onClickAway || $[50] !== options || $[51] !== toggle) {
    t12 = selectedInputValue_0 => {
      const value_0 = selectedInputValue_0?.toLocaleLowerCase();
      const optionSelected = options.find(option_0 => option_0.value.toLocaleLowerCase() === value_0 || option_0.label.toLocaleLowerCase() === value_0);
      if (!freeInput && optionSelected === undefined) {
        setSelectedInputValue("");
      }
      onClickAway?.(selectedInputValue_0);
      setQuery(undefined);
      setFocusedIndex(0);
      setEditionMode(false);
      isDropdownOpen && toggle();
    };
    $[47] = freeInput;
    $[48] = isDropdownOpen;
    $[49] = onClickAway;
    $[50] = options;
    $[51] = toggle;
    $[52] = t12;
  } else {
    t12 = $[52];
  }
  const handleClickAway = t12;
  let t13;
  if ($[53] !== freeInput || $[54] !== name || $[55] !== onChange || $[56] !== selectedInputValue || $[57] !== toggle) {
    t13 = value_1 => {
      const selectedValue = freeInput && !value_1 ? createOption(selectedInputValue) : value_1;
      setQuery("");
      onChange?.(name, selectedValue);
      setFocusedIndex(0);
      setEditionMode(false);
      toggle();
    };
    $[53] = freeInput;
    $[54] = name;
    $[55] = onChange;
    $[56] = selectedInputValue;
    $[57] = toggle;
    $[58] = t13;
  } else {
    t13 = $[58];
  }
  const handleOnChange = t13;
  let t14;
  if ($[59] !== editionMode || $[60] !== freeInput || $[61] !== isDropdownOpen || $[62] !== name || $[63] !== onChange || $[64] !== onInputChange || $[65] !== toggle) {
    t14 = event_0 => {
      const value_2 = event_0.target.value;
      if (!editionMode) {
        setEditionMode(true);
        !freeInput && onChange?.(name, null);
        setSelectedInputValue("");
      }
      onInputChange?.(event_0);
      setSelectedInputValue(value_2);
      setQuery(value_2);
      setFocusedIndex(0);
      freeInput && onChange?.(name, createOption(value_2));
      !isDropdownOpen && toggle();
    };
    $[59] = editionMode;
    $[60] = freeInput;
    $[61] = isDropdownOpen;
    $[62] = name;
    $[63] = onChange;
    $[64] = onInputChange;
    $[65] = toggle;
    $[66] = t14;
  } else {
    t14 = $[66];
  }
  const handleOnInputChange = t14;
  let t15;
  if ($[67] !== name || $[68] !== onChange || $[69] !== onClear) {
    t15 = event_1 => {
      onChange?.(name, null);
      onClear?.(event_1);
    };
    $[67] = name;
    $[68] = onChange;
    $[69] = onClear;
    $[70] = t15;
  } else {
    t15 = $[70];
  }
  const handleOnClear = t15;
  let t16;
  if ($[71] !== filteredOptions || $[72] !== focusedIndex || $[73] !== handleOnChange) {
    t16 = event_2 => {
      handleOnChange(filteredOptions[focusedIndex]);
      setEditionMode(false);
      event_2.preventDefault();
    };
    $[71] = filteredOptions;
    $[72] = focusedIndex;
    $[73] = handleOnChange;
    $[74] = t16;
  } else {
    t16 = $[74];
  }
  const handleOnEnter = t16;
  let t17;
  if ($[75] !== filteredOptions) {
    t17 = selectedOption => {
      if (typeof selectedOption === "string") {
        return;
      }
      const selectedOptionIndex = filteredOptions.findIndex(option_1 => option_1.value === selectedOption.value);
      setFocusedIndex(selectedOptionIndex);
    };
    $[75] = filteredOptions;
    $[76] = t17;
  } else {
    t17 = $[76];
  }
  const handleMouseEnter = t17;
  let t18;
  if ($[77] !== filteredOptions) {
    t18 = () => {
      setFocusedIndex(current => filteredOptions[current + 1] ? current + 1 : current);
    };
    $[77] = filteredOptions;
    $[78] = t18;
  } else {
    t18 = $[78];
  }
  const handleArrowDown = t18;
  let t19;
  if ($[79] !== filteredOptions) {
    t19 = () => {
      setFocusedIndex(current_0 => filteredOptions[current_0 - 1] ? current_0 - 1 : current_0);
    };
    $[79] = filteredOptions;
    $[80] = t19;
  } else {
    t19 = $[80];
  }
  const handleArrowUp = t19;
  let t20;
  if ($[81] !== isDropdownOpen) {
    t20 = event_3 => {
      isDropdownOpen && event_3.preventDefault();
    };
    $[81] = isDropdownOpen;
    $[82] = t20;
  } else {
    t20 = $[82];
  }
  const handleOnTab = t20;
  let t21;
  if ($[83] !== isDropdownOpen || $[84] !== toggle) {
    t21 = () => {
      isDropdownOpen && toggle();
      setEditionMode(false);
    };
    $[83] = isDropdownOpen;
    $[84] = toggle;
    $[85] = t21;
  } else {
    t21 = $[85];
  }
  const handleOnEscape = t21;
  let t22;
  if ($[86] !== handleArrowDown || $[87] !== handleArrowUp || $[88] !== handleOnEnter || $[89] !== handleOnEscape || $[90] !== handleOnTab) {
    t22 = {
      onArrowDown: handleArrowDown,
      onArrowUp: handleArrowUp,
      onTab: handleOnTab,
      onEnter: handleOnEnter,
      onEscape: handleOnEscape
    };
    $[86] = handleArrowDown;
    $[87] = handleArrowUp;
    $[88] = handleOnEnter;
    $[89] = handleOnEscape;
    $[90] = handleOnTab;
    $[91] = t22;
  } else {
    t22 = $[91];
  }
  const {
    handleKeyPress
  } = useKeyPress(t22);
  let t23;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = el => setInputElement(el as HTMLElement | null);
    $[92] = t23;
  } else {
    t23 = $[92];
  }
  const t24 = editionMode ? selectedInputValue : externalValue;
  const t25 = onClear && handleOnClear;
  let t26;
  if ($[93] !== name) {
    t26 = concatenateAndReplace(name);
    $[93] = name;
    $[94] = t26;
  } else {
    t26 = $[94];
  }
  let t27;
  if ($[95] !== disabled || $[96] !== handleKeyPress || $[97] !== handleOnClick || $[98] !== handleOnInputChange || $[99] !== label || $[100] !== labelLeftAligned || $[101] !== leftContent || $[102] !== name || $[103] !== onFocusInput || $[104] !== placeholder || $[105] !== readOnly || $[106] !== rest || $[107] !== rightIcon || $[108] !== t24 || $[109] !== t25 || $[110] !== t26) {
    t27 = <Input autoComplete="off" leftContent={leftContent} disabled={disabled} label={label} labelLeftAligned={labelLeftAligned} name={name} placeholder={placeholder} readOnly={readOnly} ref={t23} rightContent={rightIcon} value={t24} onChange={handleOnInputChange} onClear={t25} onClick={handleOnClick} onFocus={onFocusInput} onKeyDown={handleKeyPress} data-testid={t26} {...rest} />;
    $[95] = disabled;
    $[96] = handleKeyPress;
    $[97] = handleOnClick;
    $[98] = handleOnInputChange;
    $[99] = label;
    $[100] = labelLeftAligned;
    $[101] = leftContent;
    $[102] = name;
    $[103] = onFocusInput;
    $[104] = placeholder;
    $[105] = readOnly;
    $[106] = rest;
    $[107] = rightIcon;
    $[108] = t24;
    $[109] = t25;
    $[110] = t26;
    $[111] = t27;
  } else {
    t27 = $[111];
  }
  let t28;
  if ($[112] !== dataId || $[113] !== dropdownWidth || $[114] !== filteredOptions || $[115] !== focusedIndex || $[116] !== handleClickAway || $[117] !== handleMouseEnter || $[118] !== handleOnChange || $[119] !== inputElement || $[120] !== keyId || $[121] !== maxVisibleOptions || $[122] !== noResultsLabel || $[123] !== optionsHeaderText || $[124] !== placement || $[125] !== query || $[126] !== showDropdown || $[127] !== value?.value) {
    t28 = query && <DropdownOptionList data-id={dataId} dropdownWidth={dropdownWidth} isOpen={showDropdown ? showDropdown : false} optionsHeaderText={optionsHeaderText} placement={placement} reference={inputElement} maxVisibleOptions={maxVisibleOptions} onChange={handleOnChange} onClickAway={handleClickAway}>{filteredOptions.length > 0 ? filteredOptions.map((item, index) => <Option focused={filteredOptions[focusedIndex]?.value === item.value} key={getOptionIdentifier(item, index, keyId)} value={item} selected={value?.value === item.value} onMouseEnter={handleMouseEnter} data-testid={`select-autocomplete-option-${getOptionIdentifier(item, index, keyId)}`}><div>{item?.leftContent}<Highlight query={query} className={item?.leftContent ? "ml-2" : ""}>{item.label}</Highlight></div></Option>) : <Option value="">{noResultsLabel}</Option>}</DropdownOptionList>;
    $[112] = dataId;
    $[113] = dropdownWidth;
    $[114] = filteredOptions;
    $[115] = focusedIndex;
    $[116] = handleClickAway;
    $[117] = handleMouseEnter;
    $[118] = handleOnChange;
    $[119] = inputElement;
    $[120] = keyId;
    $[121] = maxVisibleOptions;
    $[122] = noResultsLabel;
    $[123] = optionsHeaderText;
    $[124] = placement;
    $[125] = query;
    $[126] = showDropdown;
    $[127] = value?.value;
    $[128] = t28;
  } else {
    t28 = $[128];
  }
  let t29;
  if ($[129] !== t27 || $[130] !== t28) {
    t29 = <>{t27}{t28}</>;
    $[129] = t27;
    $[130] = t28;
    $[131] = t29;
  } else {
    t29 = $[131];
  }
  return t29;
};
function getOptionIdentifier(item: ISelectAutocompleteOption, index: number, keyId?: string): string {
  const rawValue = keyId != null ? (item as Record<string, unknown>)[keyId] : undefined;
  return rawValue == null ? String(index) : String(rawValue);
}
function createOption(value: string): ISelectAutocompleteOption {
  return {
    label: value,
    value
  };
}